/**
 * Breadcrumb component inspired by shadcn/ui
 * @see https://ui.shadcn.com/docs/components/breadcrumb
 */
import {
  LitheElement,
  LitheFC,
  LitheSpecificElement,
} from '@wheelbox/lithejs-engine';
import { ChevronRightIcon, EllipsisIcon } from '@wheelbox/lithejs-lucide';
import { createId, clsx } from '../../utils';

// Default separator - ChevronRight icon
const DefaultSeparator = (): LitheSpecificElement => ChevronRightIcon({});

type BreadcrumbProps = {
  classes?: string | string[];
};

const Breadcrumb: LitheFC<BreadcrumbProps> = (
  { id = createId(), testId = 'breadcrumb', classes },
  ...children
): LitheElement<'nav'> => [
  'nav',
  {
    id,
    'data-testid': testId,
    'data-slot': 'breadcrumb',
    'aria-label': 'breadcrumb',
    class: [...clsx(classes)],
  },
  ...children,
];

type BreadcrumbListProps = {
  classes?: string | string[];
};

const BreadcrumbList: LitheFC<BreadcrumbListProps> = (
  { id = createId(), testId = 'breadcrumb-list', classes },
  ...children
): LitheElement<'ol'> => [
  'ol',
  {
    id,
    'data-testid': testId,
    'data-slot': 'breadcrumb-list',
    class: [
      'flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5',
      ...clsx(classes),
    ],
  },
  ...children,
];

type BreadcrumbItemProps = {
  classes?: string | string[];
};

const BreadcrumbItem: LitheFC<BreadcrumbItemProps> = (
  { id = createId(), testId = 'breadcrumb-item', classes },
  ...children
): LitheElement<'li'> => [
  'li',
  {
    id,
    'data-testid': testId,
    'data-slot': 'breadcrumb-item',
    class: ['inline-flex items-center gap-1.5', ...clsx(classes)],
  },
  ...children,
];

type BreadcrumbLinkProps = {
  href?: string;
  classes?: string | string[];
};

const BreadcrumbLink: LitheFC<BreadcrumbLinkProps> = (
  { id = createId(), testId = 'breadcrumb-link', href = '#', classes },
  ...children
): LitheElement<'a'> => [
  'a',
  {
    id,
    'data-testid': testId,
    'data-slot': 'breadcrumb-link',
    href,
    class: ['transition-colors hover:text-foreground', ...clsx(classes)],
  },
  ...children,
];

type BreadcrumbPageProps = {
  classes?: string | string[];
};

const BreadcrumbPage: LitheFC<BreadcrumbPageProps> = (
  { id = createId(), testId = 'breadcrumb-page', classes },
  ...children
): LitheElement<'span'> => [
  'span',
  {
    id,
    'data-testid': testId,
    'data-slot': 'breadcrumb-page',
    role: 'link',
    'aria-disabled': 'true',
    'aria-current': 'page',
    class: ['font-normal text-foreground', ...clsx(classes)],
  },
  ...children,
];

type BreadcrumbSeparatorProps = {
  classes?: string | string[];
};

const BreadcrumbSeparator: LitheFC<BreadcrumbSeparatorProps> = (
  { id = createId(), testId = 'breadcrumb-separator', classes },
  ...children
): LitheElement<'li'> => [
  'li',
  {
    id,
    'data-testid': testId,
    'data-slot': 'breadcrumb-separator',
    role: 'presentation',
    'aria-hidden': 'true',
    class: ['[&>svg]:size-3.5', ...clsx(classes)],
  },
  ...(children.length > 0 ? children : [DefaultSeparator]),
];

type BreadcrumbEllipsisProps = {
  classes?: string | string[];
};

const BreadcrumbEllipsis: LitheFC<BreadcrumbEllipsisProps> = ({
  id = createId(),
  testId = 'breadcrumb-ellipsis',
  classes,
}): LitheElement<'span'> => [
  'span',
  {
    id,
    'data-testid': testId,
    'data-slot': 'breadcrumb-ellipsis',
    role: 'presentation',
    'aria-hidden': 'true',
    class: ['flex size-9 items-center justify-center', ...clsx(classes)],
  },
  EllipsisIcon({ classes: 'size-4' }),
  ['span', { class: 'sr-only' }, 'More'],
];

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
  type BreadcrumbProps,
  type BreadcrumbListProps,
  type BreadcrumbItemProps,
  type BreadcrumbLinkProps,
  type BreadcrumbPageProps,
  type BreadcrumbSeparatorProps,
  type BreadcrumbEllipsisProps,
};

export default Breadcrumb;
